import React, { memo } from 'react';

import { Alert, Button, Modal, Typography } from 'antd';

import { CONSOLE_URL } from 'settings';

interface ModalConfirmWebhookCreationProps {
  opened: boolean;
  onClose: () => void;
  onContinue: () => void;
}

const HELP_MIGRATION_URL = 'https://ajuda.eduzz.com/como-migrar-minhas-integra%C3%A7%C3%B5es-para-a-developer-hub-';

const ModalConfirmWebhookCreation = memo(({ opened, onClose, onContinue }: ModalConfirmWebhookCreationProps) => {
  const [showSecondAlert, setShowSecondAlert] = React.useState(false);

  function handleShowHideSecondAlert() {
    setShowSecondAlert(!showSecondAlert);
  }

  function handleShowNewWebhook() {
    if (showSecondAlert) {
      handleShowHideSecondAlert();
    }

    onClose();
    return window.open(`${CONSOLE_URL}/webhook/configs`, '_blank');
  }

  function handleKeepOldWebhook() {
    handleShowHideSecondAlert();
    onClose();
    return onContinue();
  }

  function getModalContent() {
    if (!showSecondAlert) {
      return (
        <div className='flex flex-col gap-4'>
          <Alert
            type='warning'
            showIcon
            message='Cadastro de novos webhooks ficará indisponível nesta versão'
            description='A criação de webhooks agora deve ser feita no Developer Hub, nosso novo ambiente para integrações.'
          />

          <div className='rounded-(--uizz-antd-border-radius-lg) bg-(--uizz-antd-color-fill-tertiary) p-4'>
            <Typography.Text strong className='mb-2 block'>
              No Developer Hub você encontra:
            </Typography.Text>
            <ul className='mb-0 list-disc pl-5'>
              <li>Redisparo em massa de eventos</li>
              <li>Mais agilidade e facilidade na configuração</li>
              <li>Mais eficiência para lançamentos e grandes volumes</li>
              <li>Canal no Discord para desenvolvedores</li>
            </ul>
          </div>

          <Typography.Paragraph className='mb-0'>
            O webhook atual será <Typography.Text strong>descontinuado em 11/01/2027</Typography.Text>. Recomendamos
            realizar a migração com antecedência para garantir o funcionamento contínuo das suas integrações.
          </Typography.Paragraph>

          <div className='flex flex-col gap-1'>
            <Typography.Link href={`${CONSOLE_URL}/webhook/configs`} target='_blank' rel='noopener noreferrer'>
              Acessar o novo Webhook no Developer Hub
            </Typography.Link>
            <Typography.Link href={HELP_MIGRATION_URL} target='_blank' rel='noopener noreferrer'>
              Ver artigo completo sobre a mudança
            </Typography.Link>
          </div>
        </div>
      );
    }

    return (
      <div className='flex flex-col gap-4'>
        <Alert
          type='warning'
          showIcon
          message='Você está prestes a criar uma integração em uma versão que será descontinuada'
          description={
            <>
              A versão atual do webhook será <Typography.Text strong>desativada em 11/01/2027</Typography.Text>.
            </>
          }
        />

        <Typography.Paragraph className='mb-0'>
          Continuar usando essa versão pode gerar <Typography.Text strong>retrabalho futuro</Typography.Text>, além de{' '}
          <Typography.Text strong>limitações técnicas</Typography.Text> em comparação com o novo sistema já disponível
          no <Typography.Text strong>Developer Hub</Typography.Text>.
        </Typography.Paragraph>

        <div className='rounded-(--uizz-antd-border-radius-lg) bg-(--uizz-antd-color-fill-tertiary) p-4'>
          <Typography.Text strong className='mb-2 block'>
            No novo webhook você conta com:
          </Typography.Text>
          <ul className='mb-0 list-disc pl-5'>
            <li>Redisparo em massa de eventos</li>
            <li>Histórico inteligente e rastreável</li>
            <li>Mais confiabilidade em grandes operações</li>
          </ul>
        </div>

        <Typography.Paragraph className='mb-0'>
          Para garantir a melhor experiência e evitar ajustes futuros,{' '}
          <Typography.Text strong>recomendamos migrar agora</Typography.Text>.
        </Typography.Paragraph>
      </div>
    );
  }

  return (
    <Modal
      open={opened}
      onCancel={showSecondAlert ? handleKeepOldWebhook : handleShowHideSecondAlert}
      title='Desativação da versão atual do Webhook'
      width={640}
      maskClosable={false}
      footer={
        <div className='flex flex-wrap items-center justify-end gap-2'>
          <Button type='text' onClick={showSecondAlert ? handleKeepOldWebhook : handleShowHideSecondAlert}>
            {showSecondAlert ? 'Criar mesmo assim' : 'Continuar na versão atual'}
          </Button>
          <Button type='primary' onClick={handleShowNewWebhook}>
            {showSecondAlert ? 'Usar a nova versão' : 'Acessar Developer Hub'}
          </Button>
        </div>
      }
    >
      {getModalContent()}
    </Modal>
  );
});

export default ModalConfirmWebhookCreation;
